package Structs


import Utils.InverseBanks
import Utils.PayMethod
import Utils.RememberedCheque
import Utils.rememberedBankCode
import Utils.fixJson
import Utils.getJsonWithConfig
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import myName
import kotlin.js.JsName

@myName("PaymentData")
@Serializable
data class PaymentData(
    val value: Float,
    val pay_method: Int = 0,
    val bank_id: String = "",
    val bank_branch: String = "",
    val bank_number: String = "",
    val check_number: String = "",
    val check_date: String = "",
    val paid: Int? = null,
    var paid_date: String? = null,
    val paid_details: String? = null,
    val notes: String? = null,
    val category: String? = null,
    val canceled: Int = 0,
    /**
     * Stable id inside one document's `payment_data_raw` array. `0` means unset
     * (legacy rows); identity then falls back to array index. Assigned lazily on
     * write and on deposit splits so partial deposits do not scramble addressing.
     */
    val inner_id: Int = 0,
) {
    var payMethod: PayMethod = pay_method.let { PayMethod.CASH.convert(it) } ?: PayMethod.CASH
    var id: Int? = null
    var client_id: Int? = null
    var document_date: String? = null
    var date: String? = null
    var agent: String? = null
    init {
        if(paid_date!=null){
            val dd = paid_date!!.split(" ")
            val datep = dd[0].split("-")
            if(datep.size == 3 && datep[2].length == 4){
                paid_date = "${datep[2]}-${datep[1]}-${datep[0]}"
            }
        }
    }
    @myName("bankName")
    fun bankName(): String {
        return if (bank_id.isNotEmpty())
            InverseBanks[bank_id.toInt()] ?: ""
        else
            ""
    }

    fun toJson(): JsonObject {
        val map: MutableMap<String, JsonPrimitive> = mutableMapOf()
        map["bank_branch"] = JsonPrimitive(bank_branch)
        map["value"] = JsonPrimitive(value.toString())
        map["bank_id"] = JsonPrimitive(bank_id)
        map["bank_number"] = JsonPrimitive(bank_number)
        map["check_number"] = JsonPrimitive(check_number)
        map["check_date"] = JsonPrimitive(check_date)
        map["pay_method"] = JsonPrimitive(pay_method.toString())

        paid?.let { map["paid"] = JsonPrimitive(paid.toString()) }
        paid_date?.let { map["paid_date"] = JsonPrimitive(paid_date.toString()) }
        paid_details?.let { map["paid_details"] = JsonPrimitive(paid_details.toString()) }
        notes?.let { map["notes"] = JsonPrimitive(notes.toString()) }
        category?.let { map["category"] = JsonPrimitive(category.toString()) }
        map["canceled"] = JsonPrimitive(canceled.toString())
        if (inner_id > 0) map["inner_id"] = JsonPrimitive(inner_id.toString())

        return JsonObject(map)
    }

    companion object {
        fun createFromJson(js: String): List<PaymentData> {

            val arr = getJsonWithConfig().parseToJsonElement((js).replace("\\'", "'")) as JsonArray
            val res: List<PaymentData> = arr.map {
                getJsonWithConfig().decodeFromJsonElement(PaymentData.serializer(), it)
            }
            return res
        }

        /** Next unused [inner_id] for this document's payment list (never reuses). */
        fun nextInnerId(payments: List<PaymentData>): Int =
            (payments.maxOfOrNull { it.inner_id } ?: 0) + 1

        /**
         * Assign [inner_id] to every row that still has `0`, leaving existing ids
         * untouched. Safe to call repeatedly; used before serializing
         * `payment_data_raw` so deposits can address rows after array inserts.
         */
        fun ensureInnerIds(payments: List<PaymentData>): List<PaymentData> {
            if (payments.isEmpty()) return payments
            if (payments.all { it.inner_id > 0 }) return payments
            var next = nextInnerId(payments)
            return payments.map { payment ->
                if (payment.inner_id > 0) payment
                else {
                    val assigned = next
                    next += 1
                    payment.copy(inner_id = assigned)
                }
            }
        }

        /**
         * Copy deposit identity from [existing] onto [incoming] when the client
         * omitted it. Explicit `paid = 0` / `paid = 1` on [incoming] wins so
         * deposit stamp / cancel / CLOSE_PAYMENT keep working.
         */
        fun overlayPersistedIdentity(incoming: PaymentData, existing: PaymentData): PaymentData {
            val innerId = if (incoming.inner_id > 0) incoming.inner_id else existing.inner_id
            return if (incoming.paid != null) {
                incoming.copy(inner_id = innerId)
            } else {
                incoming.copy(
                    paid = existing.paid,
                    paid_date = existing.paid_date,
                    paid_details = existing.paid_details,
                    inner_id = innerId,
                )
            }
        }

        /**
         * Receipt / tax-invoice saves often rebuild [PaymentData] without `paid`
         * or `inner_id`. Match each incoming row to the stored list and put those
         * fields back so a cheque-number edit cannot reopen a deposited row.
         *
         * Match order: [inner_id], then cheque number + pay method, then the same
         * array index if that stored row is still unused. Deleted rows are not
         * resurrected. New unmatched rows get a fresh [inner_id] from
         * [ensureInnerIds].
         */
        fun retainPersistedFields(
            incoming: List<PaymentData>,
            existing: List<PaymentData>,
        ): List<PaymentData> {
            if (incoming.isEmpty() || existing.isEmpty()) return ensureInnerIds(incoming)
            val unused = existing.toMutableList()
            fun take(pred: (PaymentData) -> Boolean): PaymentData? {
                val index = unused.indexOfFirst(pred)
                if (index < 0) return null
                return unused.removeAt(index)
            }
            val merged = incoming.mapIndexed { index, row ->
                val found = when {
                    row.inner_id > 0 -> take { it.inner_id == row.inner_id }
                    row.check_number.isNotBlank() -> take {
                        it.pay_method == row.pay_method && it.check_number == row.check_number
                    }
                    else -> null
                } ?: existing.getOrNull(index)?.let { candidate ->
                    val unusedIndex = unused.indexOfFirst { it === candidate }
                    if (unusedIndex >= 0) unused.removeAt(unusedIndex) else null
                }
                if (found == null) row else overlayPersistedIdentity(row, found)
            }
            return ensureInnerIds(merged)
        }

        fun toJsonArrayString(payments: List<PaymentData>): String {
            val ensured = ensureInnerIds(payments)
            return if (ensured.isEmpty()) {
                "[]"
            } else {
                val payload = ensured.map {
                    Json.encodeToJsonElement(JsonObject.serializer(), it.toJson()).toString()
                }.joinToString(",")
                return "[$payload]"
            }

        }

        fun toJsonArrayElement(payments: List<PaymentData>): JsonArray {
            val ensured = ensureInnerIds(payments)
            return if (ensured.isEmpty()) {
                JsonArray(emptyList())
            } else {
                JsonArray(ensured.map {
                    Json.encodeToJsonElement(JsonObject.serializer(), it.toJson())
                })
            }

        }
    }


}

/** Row hint for receipts: check number, bank, branch, account. */
fun PaymentData.displayMetaHint(): String {
    val parts = mutableListOf<String>()
    val num = check_number.trim()
    if (num.isNotEmpty()) parts.add("מס' צק $num")
    val bank = bankName().ifBlank {
        val raw = bank_id.trim()
        if (raw.isNotEmpty() && raw != "-1") raw else ""
    }
    if (bank.isNotEmpty()) parts.add("בנק $bank")
    val branch = bank_branch.trim()
    if (branch.isNotEmpty()) parts.add("סניף $branch")
    val account = bank_number.trim()
    if (account.isNotEmpty()) parts.add("חשבון $account")
    return parts.joinToString(" · ")
}

fun PaymentData.cancelChequePrompt(): String {
    val num = check_number.trim()
    return if (num.isNotEmpty()) "האם לבטל צ'ק מספר $num?" else "האם לבטל את הצ'ק?"
}

fun PaymentData.hasRememberableChequeFields(): Boolean =
    bank_id.isNotBlank() || bank_number.isNotBlank() || bank_branch.isNotBlank()

/** Last cheque / transfer worth storing as client memory (bank + account + branch). */
fun PaymentData.isRememberableCheque(): Boolean {
    if (!hasRememberableChequeFields()) return false
    return payMethod.usesBank() ||
        pay_method == PayMethod.CHECK.state ||
        pay_method == PayMethod.FAST_CHECK.state ||
        pay_method == PayMethod.BANK.state
}

fun List<PaymentData>.firstRememberedCheque(): PaymentData? =
    firstOrNull { it.isRememberableCheque() } ?: firstOrNull { it.hasRememberableChequeFields() }

fun PaymentData.toRememberedCheque(): RememberedCheque =
    RememberedCheque(
        bankId = rememberedBankCode(bank_id),
        account = bank_number,
        branch = bank_branch,
    )
